import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
//import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { Datasource, DataSourceStatus } from '@Core/services/proprietary/datasource';
import { DocumentFileService } from '@Core/services/proprietary/document-file.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import {
    AddFiling,
    AddTask,
    DeleteDocument,
    DeleteFiling,
    DeleteNewFilings,
    DeleteTask,
    ResetState,
    SetDocuments,
    UpdateDocument,
    UpdateFiling,
    UpdateFilingReferences,
    UpdateTask
} from './multidocument-object.actions';
import { MultiDocumentObjectState } from './multidocument-object.state';

export enum EventType {
    Update = 'Update',
    Create = 'Create'
}

@Injectable()
export class MultiDocumentObjectService implements OnDestroy {
    taskID: string = '';

    public dsDocument: Datasource;

    dsDocumentStatusSub: Subscription;
    dsDocumentProcessSub: Subscription;
    dsTaskProcessSub: Subscription;
    dsTaskGetSub: Subscription;

    constructor(
        private store: Store,
        private app: AppService,
        private ds: DataService,
        private helper: StateHelperService,
        private router: Router,
        private documentFile: DocumentFileService
    ) {
        this.dsDocument = this.ds.addNewDatasource('Document', 'NO-CACHE');
    }

    ngOnDestroy() {
        if (this.dsDocumentStatusSub) {
            this.dsDocumentStatusSub.unsubscribe();
        }
        if (this.dsDocumentProcessSub) {
            this.dsDocumentProcessSub.unsubscribe();
        }
        if (this.dsTaskProcessSub) {
            this.dsTaskProcessSub.unsubscribe();
        }
        if (this.dsTaskGetSub) {
            this.dsTaskGetSub.unsubscribe();
        }
    }

    async processDocument(document: any) {
        return new Promise(async (resolve) => {
            let filings = this.store.selectSnapshot(MultiDocumentObjectState.filings);

            if (this.dsDocumentStatusSub) {
                this.dsDocumentStatusSub.unsubscribe();
                this.documentFile.dsDocument.reset();
            }

            this.dsDocumentStatusSub = this.documentFile.dsDocument.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (this.dsDocumentStatusSub) {
                            this.dsDocumentStatusSub.unsubscribe();
                        }
                        resolve(true);
                        break;

                    case DataSourceStatus.Error:
                        if (this.dsDocumentStatusSub) {
                            this.dsDocumentStatusSub.unsubscribe();
                        }
                        resolve(false);
                        break;
                }
            });

            this.documentFile.addNewDocumentWithFiling(document, filings);
        });
    }

    async deleteDoc(document: any) {
        return new Promise(async (resolve) => {
            this.removeDsDocumentStatusSubscription();
            this.dsDocument.reset();

            this.dsDocumentStatusSub = this.dsDocument.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        this.removeDsDocumentStatusSubscription();
                        resolve(true);
                        break;

                    case DataSourceStatus.Error:
                        this.removeDsDocumentStatusSubscription();
                        resolve(false);
                        break;
                }
            });

            const body = {
                '?xml': { '@version': '1.0' },
                ConceptOneRequest: {
                    DocumentProcess: {
                        Book_Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
                        Book_Loc_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID,
                        Event: 'DELETE',
                        Document: { Doc_ID: document.Doc_ID }
                    }
                }
            };
            this.dsDocument.process({ json: 'CT2' }, body);
        });
    }

    removeDsDocumentStatusSubscription() {
        if (this.dsDocumentStatusSub) {
            this.dsDocumentStatusSub.unsubscribe();
        }
    }

    reset() {
        this.taskID = '';
        this.store.dispatch(new ResetState());
    }

    setDocuments(documents: any[]) {
        this.store.dispatch(new SetDocuments(documents));
    }

    updateDocument(document: any) {
        this.store.dispatch(new UpdateDocument(document));
    }

    deleteDocument(docID: string) {
        this.store.dispatch(new DeleteDocument(docID));
    }

    addFiling(filing?: any) {
        this.store.dispatch(new AddFiling(filing));
    }

    updateFiling(filing: any) {
        this.store.dispatch(new UpdateFiling(filing));
    }

    updateFilingReferences(filingTemplate: any) {
        this.store.dispatch(new UpdateFilingReferences(filingTemplate));
    }

    deleteFiling() {
        this.store.dispatch(new DeleteFiling());
    }

    deleteNewFilings() {
        this.store.dispatch(new DeleteNewFilings());
    }

    addTask(task?: any) {
        this.store.dispatch(new AddTask(task));
    }

    updateTask(task: any) {
        this.store.dispatch(new UpdateTask(task));
    }

    deleteTask(taskID: string) {
        this.store.dispatch(new DeleteTask(taskID));
    }
}
